import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Tag, Calendar, MessageCircle,
  CheckCircle, User, Star, Globe, Shield, Phone, Mail, ChevronRight, ChevronLeft, Bookmark
} from "lucide-react";
import {
  getPublicListingDetail, createBooking, getMarketplaceProfile, getPublicProfile,
  revealListingContact,
  saveListing, unsaveListing,
  MarketplaceListing, CreateBookingPayload, MarketplaceProfile, MarketplaceListingImage,
  getListingAvailability
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import BrandDatePicker from "../../atoms/Utlis/BrandDatePicker";
import { resolveImage } from "../../../Utils/helper";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import { mpTrack } from "../../../analytics/MarketplaceTracker";
import { MarketplaceReviewsSection } from "./MarketplaceReviewsSection";
import { checkProfileCompleteness } from "./MarketplaceShared";
import MarketplaceLoader from "./MarketplaceLoader";

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TYPE_COLOR: Record<string, string> = {
  sale: "#7c4dff", rent: "#0ea5e9", photographer: "#c94daa", videographer: "#f09a3e",
};


const isLoggedIn = () => !!localStorage.getItem("access_token");

interface BookingForm {
  date_from: string;
  date_to: string;
  quantity: string;
  customer_notes: string;
}

const MarketplaceListingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<MarketplaceListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingForm, setBookingForm] = useState<BookingForm>({
    date_from: "", date_to: "", quantity: "1", customer_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);
  const [bookingPhone, setBookingPhone] = useState<string>("");
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(isLoggedIn());

  const [sellerProfile, setSellerProfile] = useState<MarketplaceProfile | null>(null);
  const [showContact, setShowContact] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [availability, setAvailability] = useState<any>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [currentCalDate, setCurrentCalDate] = useState(() => new Date());
  const [selectedStart, setSelectedStart] = useState<Date | null>(null);
  const [selectedEnd, setSelectedEnd] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const formatDateToStr = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const parseDateStr = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  const handleInputChange = (field: 'date_from' | 'date_to', val: string) => {
    setBookingForm(p => ({ ...p, [field]: val }));
    
    if (field === 'date_from') {
      const parsed = parseDateStr(val);
      if (parsed) {
        setSelectedStart(parsed);
      } else {
        setSelectedStart(null);
      }
    } else {
      const parsed = parseDateStr(val);
      if (parsed) {
        setSelectedEnd(parsed);
      } else {
        setSelectedEnd(null);
      }
    }
  };

  const handleDateSelect = (date: Date) => {
    const dateStr = formatDateToStr(date);

    if (availability?.unavailable_dates?.includes(dateStr)) {
      return;
    }

    if (!selectedStart || (selectedStart && selectedEnd)) {
      setSelectedStart(date);
      setSelectedEnd(null);
      setBookingForm(prev => ({ ...prev, date_from: dateStr, date_to: "" }));
    } else {
      if (date < selectedStart) {
        setSelectedStart(date);
        setBookingForm(prev => ({ ...prev, date_from: dateStr, date_to: "" }));
      } else {
        let hasBlocked = false;
        let curr = new Date(selectedStart);
        while (curr <= date) {
          if (availability?.unavailable_dates?.includes(formatDateToStr(curr))) {
            hasBlocked = true;
            break;
          }
          curr.setDate(curr.getDate() + 1);
        }

        if (hasBlocked) {
          showErrorToast("Selected range overlaps with booked dates. Please choose available dates only.");
          return;
        }

        setSelectedEnd(date);
        setBookingForm(prev => ({ ...prev, date_to: dateStr }));
      }
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getPublicListingDetail(id)
      .then(data => {
        if (data) {
          let metaImages: string[] = [];
          if (data.metadata) {
            const rawUrls = data.metadata.image_urls || data.metadata.image_url;
            if (Array.isArray(rawUrls)) {
              metaImages = rawUrls;
            } else if (typeof rawUrls === "string" && rawUrls) {
              try {
                const parsed = JSON.parse(rawUrls);
                if (Array.isArray(parsed)) {
                  metaImages = parsed;
                } else {
                  metaImages = [rawUrls];
                }
              } catch {
                if (rawUrls.startsWith("[") && rawUrls.endsWith("]")) {
                  try {
                    const cleaned = rawUrls.replace(/'/g, '"');
                    const parsed = JSON.parse(cleaned);
                    if (Array.isArray(parsed)) {
                      metaImages = parsed;
                    }
                  } catch {}
                }
                if (metaImages.length === 0) {
                  metaImages = rawUrls.split(",").map((s: string) => s.trim()).filter(Boolean);
                }
              }
            }
          }

          const combinedImages: MarketplaceListingImage[] = [];

          const coverUrl = data.cover_image || data.image_url;
          if (coverUrl) {
            combinedImages.push({
              id: 'cover',
              image_url: coverUrl,
              sort_order: 0,
              created_at: new Date().toISOString()
            });
          }

          metaImages.forEach((url, idx) => {
            if (url && !combinedImages.some(img => img.image_url === url)) {
              combinedImages.push({
                id: `meta-${idx}`,
                image_url: url,
                sort_order: idx + 1,
                created_at: new Date().toISOString()
              });
            }
          });

          if (data.images && data.images.length > 0) {
            data.images.forEach(img => {
              if (img.image_url && !combinedImages.some(existing => existing.image_url === img.image_url)) {
                combinedImages.push(img);
              }
            });
          }

          data.images = combinedImages;
        }

        setListing(data);

        if (data?.is_contact_revealed) setShowContact(true);
        if (data?.is_saved) setIsSaved(true);

        mpTrack("listing_view", {
          entity_type: "listing",
          entity_id: data.id,
          path: window.location.pathname,
          properties: { listing_type: data.listing_type },
        });
        sendFirebaseEvent('Marketplace_Listing_Viewed', 'Marketplace', data.listing_type, `${data.id}:${data.title}`);

        if (data?.profile_id) {
          getPublicProfile(data.profile_id)
            .then(res => {
              if (res?.data) setSellerProfile(res.data);
            })
            .catch(() => {});
        }
      })
      .catch(() => setListing(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (id && listing && listing.listing_type === "rent") {
      setLoadingAvailability(true);
      getListingAvailability(id)
        .then(res => {
          if (res && res.success && res.data) {
            setAvailability(res.data);
          }
        })
        .catch(err => {
          console.error("Failed to load availability:", err);
        })
        .finally(() => {
          setLoadingAvailability(false);
        });
    }
  }, [id, listing]);

  useEffect(() => {
    if (isLoggedIn()) {
      setProfileLoading(true);
      getMarketplaceProfile().then(res => {
        if (res?.data) setMyProfile(res.data);
      }).catch(() => {})
        .finally(() => setProfileLoading(false));
    } else {
      setProfileLoading(false);
    }
  }, []);

  if (loading || profileLoading) {
    return <MarketplaceLoader fullScreen />;
  }

  if (!listing) {
    return (
      <PageWrapper>
        <ErrorBox>
          <h2>Listing not found</h2>
          <BackLink onClick={() => navigate("/marketplace")}>← Back to browse</BackLink>
        </ErrorBox>
      </PageWrapper>
    );
  }

  const isRentOrSale = listing.listing_type === "sale" || listing.listing_type === "rent";
  const isOwner = !!(isLoggedIn() && myProfile && listing.profile_id === myProfile.id);

  const handleToggleSave = async () => {
    if (!isLoggedIn()) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    const wasSaved = isSaved;
    setIsSaved(!wasSaved);
    setSaveLoading(true);
    try {
      if (wasSaved) {
        await unsaveListing(listing.id);
        showSuccessToast("Removed from saved items.");
      } else {
        await saveListing(listing.id);
        showSuccessToast("Listing saved to your wishlist!");
      }
    } catch {
      setIsSaved(wasSaved);
      showErrorToast("Could not update saved items. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const renderCalendar = () => {
    const year = currentCalDate.getFullYear();
    const month = currentCalDate.getMonth();

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevTotalDays = new Date(year, month, 0).getDate();

    const days = [];

    // Prev month days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = prevTotalDays - i;
      const prevDate = new Date(year, month - 1, dayNum);
      days.push({ date: prevDate, isCurrentMonth: false, dayNum });
    }

    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      const currDate = new Date(year, month, i);
      days.push({ date: currDate, isCurrentMonth: true, dayNum: i });
    }

    // Next month days to fill grid
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({ date: nextDate, isCurrentMonth: false, dayNum: i });
    }

    const todayStr = formatDateToStr(new Date());

    return (
      <CalendarContainer>
        <CalendarHeader>
          <NavBtn type="button" onClick={() => setCurrentCalDate(new Date(year, month - 1, 1))}>
            <ChevronLeft size={16} />
          </NavBtn>
          <MonthYearTitle>{monthNames[month]} {year}</MonthYearTitle>
          <NavBtn type="button" onClick={() => setCurrentCalDate(new Date(year, month + 1, 1))}>
            <ChevronRight size={16} />
          </NavBtn>
        </CalendarHeader>
        <WeekDaysGrid>
          {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
            <WeekDayName key={idx}>{day}</WeekDayName>
          ))}
        </WeekDaysGrid>
        <DaysGrid>
          {days.map((item, idx) => {
            const dateStr = formatDateToStr(item.date);
            const isUnavailable = availability?.unavailable_dates?.includes(dateStr);
            const isPast = dateStr < todayStr;
            const isFutureUnavailable = !isPast && !!isUnavailable;
            const isDisabled = isPast || isUnavailable;

            const isStart = !!(selectedStart && formatDateToStr(selectedStart) === dateStr);
            const isEnd = !!(selectedEnd && formatDateToStr(selectedEnd) === dateStr);
            const isSelected = isStart || isEnd;

            let isInRange = false;
            if (selectedStart && selectedEnd) {
              isInRange = item.date > selectedStart && item.date < selectedEnd;
            } else if (selectedStart && hoveredDate) {
              isInRange = item.date > selectedStart && item.date < hoveredDate;
            }

            return (
              <DayCell
                key={idx}
                type="button"
                $isCurrentMonth={item.isCurrentMonth}
                $isDisabled={isDisabled}
                $isSelected={isSelected}
                $isInRange={isInRange}
                $isPast={isPast}
                $isFutureUnavailable={isFutureUnavailable}
                disabled={isDisabled}
                title={isFutureUnavailable ? "Not available on this date" : isPast ? "Past date" : undefined}
                onClick={() => handleDateSelect(item.date)}
                onMouseEnter={() => selectedStart && !selectedEnd && setHoveredDate(item.date)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                <span>{item.dayNum}</span>
              </DayCell>
            );
          })}
        </DaysGrid>
      </CalendarContainer>
    );
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn()) {
      showErrorToast("Please login first to book an inquiry.");
      sendFirebaseEvent('Marketplace_Login_Prompted', 'Marketplace', 'login_prompted', 'booking');
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }

    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot create inquiries or bookings.");
      return;
    }

    if (!checkProfileCompleteness()) {
      showErrorToast("Please complete your profile details first to make bookings.");
      navigate("/marketplace/profile/create");
      return;
    }

    if (listing.listing_type === "rent") {
      const startStr = bookingForm.date_from;
      const endStr = bookingForm.date_to;

      if (!startStr || !endStr) {
        showErrorToast("Please select start and end dates.");
        return;
      }

      if (startStr > endStr) {
        showErrorToast("End date must be on or after start date.");
        return;
      }

      let start = new Date(startStr);
      let end = new Date(endStr);
      let hasBlocked = false;
      while (start <= end) {
        if (availability?.unavailable_dates?.includes(formatDateToStr(start))) {
          hasBlocked = true;
          break;
        }
        start.setDate(start.getDate() + 1);
      }

      if (hasBlocked) {
        showErrorToast("Selected dates overlap with already booked dates. Please choose available dates only.");
        return;
      }
    }

    sendFirebaseEvent('Marketplace_Booking_Initiated', 'Marketplace', listing.listing_type, `${listing.id}:${listing.title}`);
    mpTrack("inquiry_start", {
      entity_type: "listing",
      entity_id: listing.id,
      path: window.location.pathname,
    });
    setSubmitting(true);
    try {
      const payload: CreateBookingPayload = {
        listing_id: listing.id,
        customer_notes: bookingForm.customer_notes || undefined,
        quantity: isRentOrSale ? (Number(bookingForm.quantity) || 1) : undefined,
      };
      if (listing.listing_type === "rent") {
        payload.date_from = bookingForm.date_from;
        payload.date_to = bookingForm.date_to;
      }
      if (isRentOrSale) payload.quantity = Number(bookingForm.quantity) || 1;

      const booking = await createBooking(payload);
      sendFirebaseEvent('Marketplace_Booking_Submitted', 'Marketplace', listing.listing_type, listing.id);
      setBookingPhone(booking.customer_phone || "");
      setBookingSuccess(booking.reference);
      showSuccessToast("Inquiry submitted! The seller will contact you.");

      if (listing.listing_type === "rent" && id) {
        getListingAvailability(id)
          .then(res => {
            if (res && res.success && res.data) {
              setAvailability(res.data);
            }
          })
          .catch(() => {});
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || "Failed to submit booking";
      sendFirebaseEvent('Marketplace_Booking_Failed', 'Marketplace', listing.id, errMsg);
      showErrorToast(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (bookingSuccess) {
    return (
      <PageWrapper>
        <SuccessCard>
          <SuccessIcon><CheckCircle size={48} color="#16a34a" /></SuccessIcon>
          <SuccessTitle>Inquiry Submitted!</SuccessTitle>
          <SuccessRef>Booking Reference: <strong>{bookingSuccess}</strong></SuccessRef>
          <SuccessDesc>
            The seller will review your inquiry and contact you soon on your registered phone number.
            {bookingPhone && (
              <ContactNote><Phone size={13} /> Expect a call on {bookingPhone}</ContactNote>
            )}
          </SuccessDesc>
          <BackBtn onClick={() => navigate("/marketplace")}>← Browse More Listings</BackBtn>
        </SuccessCard>
      </PageWrapper>
    );
  }

  const categoryPath = listing.category_name ? listing.category_name.toUpperCase() : "";

  const specMetadata = listing.metadata
    ? Object.entries(listing.metadata).filter(
        ([k]) =>
          k.toLowerCase() !== "image_urls" &&
          k.toLowerCase() !== "image_url" &&
          k.toLowerCase() !== "resolution" &&
          k.toLowerCase() !== "frame_rate" &&
          k.toLowerCase() !== "sensor" &&
          k.toLowerCase() !== "included_items"
      )
    : [];

  const isAvailableToday = () => {
    if (listing.listing_type !== "rent") return true;
    if (!availability) return true;
    const todayStr = formatDateToStr(new Date());
    return !availability.unavailable_dates?.includes(todayStr);
  };

  return (
    <PageWrapper>
      <Container>
        <TopNav>
          <BackLink onClick={() => navigate(-1)}>
            <ArrowLeft size={16} /> Back
          </BackLink>
        </TopNav>
        <HeroSection>
          <GalleryColumn>
            <MainImageBlock>
              {isAvailableToday() && <TypeBadge>AVAILABLE NOW</TypeBadge>}
              {listing.seller?.is_verified && <VerifiedBadge><Shield size={12}/> VERIFIED PRO</VerifiedBadge>}
              
              {listing.images && listing.images.length > 0 ? (
                <img src={resolveImage(listing.images[activeImageIdx]?.image_url)} alt={listing.title} />
              ) : listing.image_url ? (
                <img src={resolveImage(listing.image_url)} alt={listing.title} />
              ) : listing.cover_image ? (
                <img src={resolveImage(listing.cover_image)} alt={listing.title} />
              ) : (
                <PlaceholderEmoji>📷</PlaceholderEmoji>
              )}
            </MainImageBlock>

            {listing.images && listing.images.length > 1 && (
              <ThumbnailsRow>
                {listing.images.map((img, idx) => (
                  <ThumbnailWrap
                    key={img.id || idx}
                    $active={activeImageIdx === idx}
                    onClick={() => setActiveImageIdx(idx)}
                  >
                    <img src={resolveImage(img.image_url)} alt={`${listing.title} thumbnail ${idx + 1}`} />
                  </ThumbnailWrap>
                ))}
              </ThumbnailsRow>
            )}
          </GalleryColumn>

          <DetailsColumn>
            {categoryPath && <CategoryPath>{categoryPath}</CategoryPath>}
            <Title>{listing.title}</Title>
            {/* Save / Wishlist button */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <button
                id="detail-save-btn"
                onClick={handleToggleSave}
                disabled={saveLoading}
                title={isSaved ? "Remove from saved" : "Save to wishlist"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 16px",
                  borderRadius: 100,
                  border: isSaved ? "none" : "1.5px solid rgba(201,77,170,0.4)",
                  background: isSaved ? "linear-gradient(135deg, #ff6b6b 0%, #c94daa 100%)" : "transparent",
                  color: isSaved ? "#fff" : "#c94daa",
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: saveLoading ? "wait" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: isSaved ? "0 2px 12px rgba(201,77,170,0.35)" : "none",
                  opacity: saveLoading ? 0.7 : 1,
                }}
              >
                <Bookmark size={14} fill={isSaved ? "#ffffff" : "none"} />
                {isSaved ? "Saved" : "Save"}
              </button>
            </div>

            {(listing.avg_rating !== undefined && listing.avg_rating > 0) && (
              <RatingRow 
                onClick={() => {
                  document.getElementById('listing-reviews-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                title="View reviews"
              >
                <div className="stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      size={14}
                      fill={star <= Math.round(listing.avg_rating!) ? "#ffb400" : "none"}
                      color={star <= Math.round(listing.avg_rating!) ? "#ffb400" : "#d1d5db"}
                    />
                  ))}
                </div>
                <div className="reviews">{listing.review_count} review{listing.review_count !== 1 ? "s" : ""}</div>
              </RatingRow>
            )}
            {listing.location && (
              <LocationWrapper>
                <MapPin size={16} />
                <span>{listing.location}</span>
              </LocationWrapper>
            )}

            {!isOwner && (
              <PricingCard>
                <form onSubmit={handleBooking}>
                  {myProfile?.is_active === false && (
                    <div
                      style={{
                        padding: "10px 14px",
                        marginBottom: "12px",
                        borderRadius: "8px",
                        background: "rgba(239, 68, 68, 0.15)",
                        border: "1px solid rgba(239, 68, 68, 0.35)",
                        color: "#fca5a5",
                        fontSize: "12px",
                        lineHeight: "1.4",
                      }}
                    >
                      ⚠️ Your marketplace account is frozen. Inquiries and bookings are disabled.
                    </div>
                  )}
                  {listing.listing_type === "rent" ? (
                    <PriceBlock $highlight={true}>
                      <div className="header">
                        <span>Rent</span>
                        <div className="price">₹{Number(listing.price || 0).toLocaleString("en-IN")}<span>/{listing.price_unit ? listing.price_unit.replace("per_", "") : "day"}</span></div>
                      </div>
                      <div className="subtext">Only professional use</div>
                      
                      <div className="date-inputs">
                         <BrandDatePicker
                           value={bookingForm.date_from}
                           onChange={(v) => handleInputChange('date_from', v)}
                           required
                         />
                         <BrandDatePicker
                           value={bookingForm.date_to}
                           onChange={(v) => handleInputChange('date_to', v)}
                           required
                         />
                      </div>

                      {renderCalendar()}

                      <GradientButton
                        type="submit"
                        disabled={submitting || myProfile?.is_active === false}
                        style={{
                          marginTop: "16px",
                          opacity: myProfile?.is_active === false ? 0.5 : 1,
                          cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                        }}
                      >
                        <Calendar size={16} />
                        {submitting
                          ? "Submitting…"
                          : myProfile?.is_active === false
                          ? "Account Frozen"
                          : isLoggedIn()
                          ? "Rent Equipment"
                          : "Login to Send Inquiry"}
                      </GradientButton>
                    </PriceBlock>
                  ) : listing.listing_type === "sale" ? (
                    <PriceBlock $highlight={false}>
                      <div className="header">
                        <span>Own</span>
                        <div className="price">₹{Number(listing.price || 0).toLocaleString("en-IN")}</div>
                      </div>
                      <div className="subtext">New in box / used</div>
                      <GradientButton
                        type="submit"
                        disabled={submitting || myProfile?.is_active === false}
                        style={{
                          opacity: myProfile?.is_active === false ? 0.5 : 1,
                          cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                        }}
                      >
                         {submitting
                           ? "Submitting…"
                           : myProfile?.is_active === false
                           ? "Account Frozen"
                           : isLoggedIn()
                           ? "Buy Now"
                           : "Login to Send Inquiry"}
                      </GradientButton>
                    </PriceBlock>
                  ) : (
                    <PriceBlock $highlight={true}>
                      <div className="header">
                        <span>Hire</span>
                        <div className="price">₹{Number(listing.price || 0).toLocaleString("en-IN")}<span>/{listing.price_unit ? listing.price_unit.replace("per_", "") : "day"}</span></div>
                      </div>
                      <GradientButton
                        type="submit"
                        disabled={submitting || myProfile?.is_active === false}
                        style={{
                          opacity: myProfile?.is_active === false ? 0.5 : 1,
                          cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                        }}
                      >
                         {submitting
                           ? "Submitting…"
                           : myProfile?.is_active === false
                           ? "Account Frozen"
                           : isLoggedIn()
                           ? "Send Inquiry"
                           : "Login to Send Inquiry"}
                      </GradientButton>
                    </PriceBlock>
                  )}
                </form>
              </PricingCard>
            )}

            {(listing.metadata?.resolution || listing.metadata?.frame_rate || listing.metadata?.sensor) && (
              <SpecsRow>
                {listing.metadata?.resolution && (
                  <SpecItem>
                    <span className="label">RESOLUTION</span>
                    <span className="value">{listing.metadata.resolution}</span>
                  </SpecItem>
                )}
                {listing.metadata?.frame_rate && (
                  <SpecItem>
                    <span className="label">FRAME RATE</span>
                    <span className="value">{listing.metadata.frame_rate}</span>
                  </SpecItem>
                )}
                {listing.metadata?.sensor && (
                  <SpecItem>
                    <span className="label">SENSOR</span>
                    <span className="value">{listing.metadata.sensor}</span>
                  </SpecItem>
                )}
              </SpecsRow>
            )}

            {listing.seller && (
              <VendorCard>
                <div className="vendor-main">
                  <div className="avatar">{(listing.seller.display_name || "V").charAt(0).toUpperCase()}</div>
                  <div className="info">
                    <div className="label">VENDOR</div>
                    <div className="name">{listing.seller.display_name}</div>
                  </div>
                </div>
                
                <div className="actions">
                  {showContact ? (
                    <div className="contact-details">
                      {listing.seller.contact_phone && <a href={`tel:${listing.seller.contact_phone}`}><Phone size={16}/> {listing.seller.contact_phone}</a>}
                      {listing.seller.contact_email && <a href={`mailto:${listing.seller.contact_email}`}><Mail size={16}/> {listing.seller.contact_email}</a>}
                    </div>
                  ) : (
                    <button className="chat-btn" onClick={async () => {
                      if (!isLoggedIn()) {
                        showErrorToast("Please login first to view contact details.");
                        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
                        return;
                      }
                      if (!checkProfileCompleteness()) {
                        showErrorToast("Please complete your profile details first to view contact details.");
                        navigate("/marketplace/profile/create");
                        return;
                      }
                      setRevealLoading(true);
                      try {
                        const result = await revealListingContact(id!);
                        setListing(prev => prev ? {
                          ...prev,
                          is_contact_revealed: result.is_contact_revealed,
                          seller: { ...prev.seller!, ...result.seller },
                        } : prev);
                        setShowContact(true);
                      } catch (err) {
                        showErrorToast("Failed to reveal contact.");
                      } finally {
                        setRevealLoading(false);
                      }
                    }}>
                      <MessageCircle size={16} /> Show Contact Details
                    </button>
                  )}
                </div>
              </VendorCard>
            )}
          </DetailsColumn>
        </HeroSection>

      </Container>
      
      <BottomSection>
        <Container>
          <TechSpecsGrid>
            <div className="main-details">
              {listing.description && (
                <div className="description-section" style={{ marginBottom: "40px" }}>
                  <div className="section-label" style={{ fontSize: "11px", color: "var(--mp-featureIconColor, #D863BA)", fontWeight: 700, letterSpacing: "1px", marginBottom: "8px", textTransform: "uppercase" }}>ABOUT THIS ITEM</div>
                  <SectionTitle>Description</SectionTitle>
                  <DescriptionText>{listing.description}</DescriptionText>
                </div>
              )}

              {(specMetadata.length > 0 || listing.location) && (
                <div className="specs">
                  <div className="section-label" style={{ fontSize: "11px", color: "var(--mp-featureIconColor, #D863BA)", fontWeight: 700, letterSpacing: "1px", marginBottom: "8px", textTransform: "uppercase" }}>DETAILS</div>
                  <SectionTitle>Technical Specifications & Location</SectionTitle>
                  <SpecList>
                    {listing.location && (
                      <div className="row">
                        <div className="key">Location</div>
                        <div className="val" style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                          <MapPin size={14} style={{ color: '#c94daa' }} />
                          {listing.location}
                        </div>
                      </div>
                    )}
                    {specMetadata.map(([k, v]) => (
                      <div className="row" key={k}>
                        <div className="key">{k.replace(/_/g, ' ')}</div>
                        <div className="val">{String(v)}</div>
                      </div>
                    ))}
                  </SpecList>
                </div>
              )}
            </div>
            
            {listing.metadata?.included_items && (
              <div className="box">
                <SectionTitle>What's in the box</SectionTitle>
                <BoxGrid>
                  {Array.isArray(listing.metadata.included_items) ? (
                    listing.metadata.included_items.map((item: any, idx: number) => (
                      <BoxItem key={idx}>
                        <div className="icon"><CheckCircle size={18} /></div>
                        <div className="text">
                          <div className="title">{String(item)}</div>
                          <div className="sub">Included Item</div>
                        </div>
                      </BoxItem>
                    ))
                  ) : (
                    <BoxItem>
                      <div className="icon"><CheckCircle size={18} /></div>
                      <div className="text">
                        <div className="title">{String(listing.metadata.included_items)}</div>
                        <div className="sub">Included Item</div>
                      </div>
                    </BoxItem>
                  )}
                </BoxGrid>
                <div className="disclaimer">
                  * May not include standard accessories depending on seller or location.
                </div>
              </div>
            )}
          </TechSpecsGrid>
          
          <SectionTitle id="listing-reviews-section">Reviews</SectionTitle>
          <MarketplaceReviewsSection
            mode="listing"
            targetId={listing.id}
            avgRating={listing.avg_rating}
            reviewCount={listing.review_count}
            isOwner={isOwner}
          />
        </Container>

      </BottomSection>
    </PageWrapper>
  );
};

export default MarketplaceListingDetailPage;

const PageWrapper = styled.div`
  background: var(--mp-background, #111111);
  color: var(--mp-textColor, #ffffff);
  min-height: 100vh;
  font-family: 'Urbanist', sans-serif;
`;

const Container = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 40px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const TopNav = styled.div`
  padding: 24px 0;
`;

const BackLink = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  background: none; border: none; cursor: pointer;
  color: var(--mp-textColor, #fff); font-size: 14px; font-weight: 600;
  &:hover { opacity: 0.8; }
`;

const HeroSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 40px;
  padding-bottom: 60px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-bottom: 30px;
  }
`;

const GalleryColumn = styled.div`
  display: flex; flex-direction: column; gap: 16px;
`;

const MainImageBlock = styled.div`
  position: relative;
  background: var(--mp-cardBackground, #1a1a1a);
  border: 1px solid var(--mp-border, rgba(255,255,255,0.1));
  border-radius: 16px;
  height: 500px;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; }

  @media (max-width: 768px) {
    height: 350px;
  }
  @media (max-width: 480px) {
    height: 250px;
  }
`;

const TypeBadge = styled.div`
  position: absolute; top: 20px; left: 20px;
  background: ${gradient}; color: #fff;
  padding: 6px 12px; border-radius: 20px;
  font-size: 11px; font-weight: 700; letter-spacing: 1px;

  @media (max-width: 480px) {
    top: 12px; left: 12px;
    font-size: 10px;
    padding: 4px 8px;
  }
`;

const VerifiedBadge = styled.div`
  position: absolute; top: 20px; left: 140px;
  background: rgba(255,255,255,0.1); backdrop-filter: blur(10px); color: #fff;
  padding: 6px 12px; border-radius: 20px; display: flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700; letter-spacing: 1px;
  border: 1px solid rgba(255,255,255,0.2);

  @media (max-width: 480px) {
    top: 12px; left: unset; right: 12px;
    font-size: 10px;
    padding: 4px 8px;
  }
`;

const PlaceholderEmoji = styled.div`font-size: 60px; opacity: 0.2; color: var(--mp-textColor, #fff);`;

const ThumbnailsRow = styled.div`
  display: flex; gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const ThumbnailWrap = styled.button<{ $active: boolean }>`
  width: 90px; height: 90px;
  flex-shrink: 0;
  border-radius: 12px; overflow: hidden;
  border: 2px solid ${p => p.$active ? 'var(--mp-textColor, #fff)' : 'transparent'};
  background: var(--mp-cardBackground, #1a1a1a); cursor: pointer; padding: 0;
  img { width: 100%; height: 100%; object-fit: contain; }

  @media (max-width: 480px) {
    width: 70px; height: 70px;
  }
`;

const DetailsColumn = styled.div`
  display: flex; flex-direction: column;
`;

const CategoryPath = styled.div`
  font-size: 11px; color: var(--mp-featureIconColor, #D863BA); font-weight: 700; letter-spacing: 1px;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-size: 36px; font-weight: 300; margin: 0 0 16px; line-height: 1.2;
  font-family: 'Playfair Display', serif; color: var(--mp-textColor, #fff);

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LocationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--mp-subColor, #888);
  margin-bottom: 20px;
  svg {
    color: #c94daa;
  }
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  cursor: pointer;
  width: max-content;
  border-radius: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
  
  .stars {
    display: flex;
    gap: 2px;
  }

  .reviews {
    color: var(--mp-subColor, #888888);
    font-family: Montserrat, sans-serif;
    font-size: 13px;
  }
`;

const PricingCard = styled.div`
  display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px;
  border: 1px solid var(--mp-border, rgba(255,255,255,0.1)); border-radius: 16px; padding: 2px;
`;

const PriceBlock = styled.div<{ $highlight: boolean }>`
  background: ${p => p.$highlight ? 'var(--mp-cardBackground, rgba(255,255,255,0.05))' : 'transparent'};
  border-radius: 14px; padding: 24px;

  @media (max-width: 480px) {
    padding: 16px;
  }
  
  .header {
    display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 8px;
    span { font-size: 16px; font-weight: 600; color: var(--mp-textColor, #fff); }
    .price { 
      font-size: 24px; font-weight: 700; color: ${p => p.$highlight ? '#c94daa' : 'var(--mp-textColor, #fff)'};
      span { font-size: 14px; font-weight: 400; color: var(--mp-subColor, #888); }
    }
  }
  .subtext { font-size: 13px; color: var(--mp-subColor, #888); margin-bottom: 20px; }
  
  .date-inputs {
    display: flex; gap: 12px; margin-bottom: 16px;
    @media (max-width: 480px) {
      flex-direction: column;
      gap: 8px;
    }
    input {
      flex: 1; background: var(--mp-bodyBackground, #000); border: 1px solid var(--mp-border, rgba(255,255,255,0.1));
      color: var(--mp-textColor, #fff); padding: 12px; border-radius: 8px; color-scheme: dark;
    }
  }
`;

const GradientButton = styled.button`
  width: 100%; padding: 16px; border: none; border-radius: 8px;
  background: var(--mp-heroBtnBg, ${gradient}); color: #fff; font-size: 15px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  cursor: pointer; &:disabled { opacity: 0.5; }
`;

const OutlinedButton = styled.button`
  width: 100%; padding: 16px; border: 1px solid var(--mp-border, rgba(255,255,255,0.2)); border-radius: 8px;
  background: transparent; color: var(--mp-textColor, #fff); font-size: 15px; font-weight: 700;
  cursor: pointer; &:disabled { opacity: 0.5; }
`;

const SpecsRow = styled.div`
  display: flex; justify-content: space-between; border-top: 1px solid var(--mp-border, rgba(255,255,255,0.1));
  border-bottom: 1px solid var(--mp-border, rgba(255,255,255,0.1)); padding: 20px 0; margin-bottom: 32px;
  gap: 12px;
  @media (max-width: 480px) {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`;

const SpecItem = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 1;
  @media (max-width: 480px) {
    flex: unset;
    width: calc(50% - 6px);
    align-items: flex-start;
  }
  .label { font-size: 10px; color: var(--mp-subColor, #888); font-weight: 700; letter-spacing: 1px; }
  .value { font-size: 16px; font-weight: 600; color: var(--mp-textColor, #fff); }
`;

const VendorCard = styled.div`
  display: flex; flex-direction: column; gap: 16px; background: var(--mp-cardBackground, rgba(255,255,255,0.03));
  padding: 16px; border-radius: 12px; border: 1px solid var(--mp-border, rgba(255,255,255,0.05));
  
  .vendor-main {
    display: flex; align-items: center; gap: 16px;
  }
  
  .avatar { width: 48px; height: 48px; border-radius: 50%; background: #333; display: flex; align-items: center; justify-content: center; font-weight: 700; color: white;}
  .info { flex: 1; }
  .label { font-size: 10px; color: var(--mp-subColor, #888); font-weight: 700; letter-spacing: 1px; margin-bottom: 4px; }
  .name { font-size: 15px; font-weight: 600; margin-bottom: 4px; color: var(--mp-textColor, #fff); }
  .stats { font-size: 12px; color: var(--mp-subColor, #888); }
  
  .actions { 
    border-top: 1px solid var(--mp-border, rgba(255,255,255,0.1)); padding-top: 12px;
    .chat-btn { 
      background: transparent; border: none; width: 100%; cursor: pointer; 
      display: flex; align-items: center; justify-content: center; gap: 8px;
      color: var(--mp-featureIconColor, #D863BA); font-weight: 600; font-size: 14px;
    }
    .contact-details { 
      display: flex; flex-direction: column; gap: 8px; 
      a { color: var(--mp-textColor, #fff); text-decoration: none; font-size: 14px; display: flex; align-items: center; gap: 8px;} 
    }
  }
`;

const BottomSection = styled.div`
  background: var(--mp-bodyBackground, #1a1a1a);
  padding-top: 80px;
  @media (max-width: 768px) {
    padding-top: 40px;
  }
`;

const TechSpecsGrid = styled.div`
  display: grid; grid-template-columns: 1fr 400px; gap: 60px; padding-bottom: 80px;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 40px; padding-bottom: 40px; }
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif; font-size: 32px; font-weight: 400; margin: 0 0 40px; color: var(--mp-textColor, #fff);
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const SpecList = styled.div`
  display: flex; flex-direction: column;
  .row { display: flex; justify-content: space-between; padding: 20px 0; border-bottom: 1px solid var(--mp-border, rgba(255,255,255,0.1)); }
  .key { color: var(--mp-subColor, #888); font-size: 14px; text-transform: capitalize; }
  .val { font-size: 14px; font-weight: 600; text-align: right; color: var(--mp-textColor, #fff); }
  .desc { font-size: 15px; color: var(--mp-subColor, #888); line-height: 1.6; }
`;

const DescriptionText = styled.p`
  font-size: 16px;
  color: var(--mp-subColor, #aaa);
  line-height: 1.8;
  margin: 0;
  white-space: pre-line;
`;

const BoxGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BoxItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  .icon { 
    width: 40px; height: 40px; background: var(--mp-heroBtnBg, #333); border-radius: 8px;
    display: flex; align-items: center; justify-content: center; color: white;
  }
  .text {
    .title { font-size: 13px; font-weight: 600; margin-bottom: 4px; color: var(--mp-textColor, #fff); }
    .sub { font-size: 11px; color: var(--mp-subColor, #888); }
  }
`;

const BannerImage = styled.div`
  height: 500px; background: url('https://images.unsplash.com/photo-1590403759902-60295da9e97f?q=80&w=2000&auto=format&fit=crop') center/cover;
  position: relative; display: flex; align-items: center; padding: 0 10%;
  &::before { content: ''; position: absolute; inset: 0; background: linear-gradient(to right, rgba(0,0,0,0.8), rgba(0,0,0,0.2)); }
  .content {
    position: relative; z-index: 1; max-width: 500px;
    h2 { font-family: 'Playfair Display', serif; font-size: 40px; margin: 0 0 20px; font-weight: 400; color: white; }
    p { font-size: 16px; color: #ccc; line-height: 1.6; margin-bottom: 30px;}
    .stats-row {
      display: flex; gap: 40px;
      .stat {
        display: flex; flex-direction: column;
        .num { font-size: 24px; color: #FFC951; font-weight: 700; }
        .label { font-size: 12px; color: #aaa; text-transform: uppercase; letter-spacing: 1px;}
      }
    }
  }
`;

const LoadingBox = styled.div`
  padding: 100px; text-align: center; color: var(--mp-subColor, #888);
`;

const ErrorBox = styled.div`
  padding: 100px; text-align: center;
  h2 { margin-bottom: 20px; color: var(--mp-textColor, #fff); }
`;

const SuccessCard = styled.div`
  max-width: 500px; margin: 100px auto; background: var(--mp-cardBackground, #1a1a1a); padding: 40px;
  border-radius: 20px; text-align: center; border: 1px solid var(--mp-border, rgba(255,255,255,0.1));
`;
const SuccessIcon = styled.div`margin-bottom: 20px;`;
const SuccessTitle = styled.h2`margin: 0 0 12px; font-size: 28px; color: var(--mp-textColor, #fff);`;
const SuccessRef = styled.div`color: var(--mp-subColor, #888); margin-bottom: 16px;`;
const SuccessDesc = styled.div`color: var(--mp-subColor, #888); line-height: 1.6; margin-bottom: 30px;`;
const ContactNote = styled.div`display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 12px; color: var(--mp-textColor, #fff); font-weight: 600;`;
const BackBtn = styled.button`background: ${gradient}; color: #fff; border: none; padding: 14px 24px; border-radius: 8px; font-weight: 700; cursor: pointer;`;
const sectionLabelStyle = `
  font-size: 11px;
  color: var(--mp-featureIconColor, #D863BA);
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const CalendarContainer = styled.div`
  margin-top: 20px;
  background: var(--mp-cardBackground, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--mp-border, rgba(255, 255, 255, 0.08));
  border-radius: 12px;
  padding: 16px;
  color: var(--mp-textColor, #fff);
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const MonthYearTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-textColor, #fff);
`;

const NavBtn = styled.button`
  background: transparent;
  border: none;
  color: var(--mp-textColor, #fff);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 50%;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const WeekDaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
  text-align: center;
`;

const WeekDayName = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: var(--mp-subColor, #888);
  text-transform: uppercase;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const DayCell = styled.button<{
  $isCurrentMonth: boolean;
  $isDisabled: boolean;
  $isSelected: boolean;
  $isInRange: boolean;
  $isPast: boolean;
  $isFutureUnavailable: boolean;
}>`
  aspect-ratio: 1;
  background: ${props => 
    props.$isSelected 
      ? 'var(--mp-heroAccent, #c94daa)' 
      : props.$isFutureUnavailable
        ? 'linear-gradient(135deg, rgba(255, 201, 81, 0.25) 0%, rgba(201, 77, 170, 0.25) 100%)'
        : props.$isInRange 
          ? 'rgba(201, 77, 170, 0.15)' 
          : 'transparent'};
  border: none;
  border-radius: 50%;
  color: ${props => 
    props.$isSelected 
      ? '#fff' 
      : props.$isFutureUnavailable
        ? 'rgba(255, 255, 255, 0.85)'
        : props.$isPast 
          ? 'var(--mp-subColor, rgba(255, 255, 255, 0.15))' 
          : props.$isCurrentMonth 
            ? 'var(--mp-textColor, #fff)' 
            : 'var(--mp-subColor, rgba(255, 255, 255, 0.35))'};
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: ${props => props.$isPast ? 'line-through' : 'none'};
  transition: all 0.2s;

  &:hover {
    background: ${props => 
      props.$isPast 
        ? 'transparent' 
        : props.$isFutureUnavailable
          ? 'linear-gradient(135deg, rgba(255, 201, 81, 0.35) 0%, rgba(201, 77, 170, 0.35) 100%)'
          : props.$isSelected 
            ? 'var(--mp-heroAccent, #c94daa)' 
            : 'rgba(255, 255, 255, 0.08)'};
  }
`;
